import { useState, forwardRef } from 'react';
import { X, Mail, Lock, ArrowRight, Loader2, Eye, EyeOff, KeyRound, Building2, User, Clock } from 'lucide-react';
import { useOnboardingPersistence } from '@/hooks/useOnboardingPersistence';
import { supabase } from '@/integrations/supabase/client';

interface ResumeOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResume: (data: {
    currentStep: string;
    accountType: string;
    onboardingData: Record<string, unknown>;
    sessionToken: string;
    email: string;
  }) => void;
}


type Step = 'account-type' | 'credentials';

export const ResumeOnboardingModal = forwardRef<HTMLDivElement, ResumeOnboardingModalProps>(function ResumeOnboardingModal({ isOpen, onClose, onResume }, ref) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [underReview, setUnderReview] = useState(false);
  const [loginMode, setLoginMode] = useState<'password' | 'otp'>('password');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [accountType, setAccountType] = useState<'individual' | 'corporate' | null>(null);
  const [step, setStep] = useState<Step>('account-type');
  const { verifyPassword, sendOTP, verifyOTP } = useOnboardingPersistence();

  const handleAccountTypeSelect = (type: 'individual' | 'corporate') => {
    setAccountType(type);
    setStep('credentials');
    setError('');
  };

  const handlePasswordLogin = async () => {
    if (!email || !email.includes('@') || !password) {
      setError('Please enter your email and password');
      return;
    }
    
    if (!accountType) {
      setError('Please select an account type first');
      setStep('account-type');
      return;
    }
    
    setIsLoading(true);
    setError('');
    setUnderReview(false);
    const result = await verifyPassword(email, password, accountType);
    setIsLoading(false);
    
    // Handle under review status
    if (result?.under_review) {
      setUnderReview(true);
      return;
    }

    // Handle specific error messages from backend
    if (result?.error) {
      setError(result.error);
      return;
    }
    
    if (!result) {
      setError('Unable to verify credentials');
      return;
    }
    
    if (result.success) {
      onResume({
        currentStep: result.current_step,
        accountType: result.account_type,
        onboardingData: result.onboarding_data,
        sessionToken: result.session_token,
        email
      });
      onClose();
    }
  };

  const handleSendOTP = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsLoading(true);
    setError('');
    const result = await sendOTP(email);
    setIsLoading(false);
    
    if (result) {
      setOtpSent(true);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length < 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    
    setIsLoading(true);
    setError('');
    const result = await verifyOTP(email, otp);
    setIsLoading(false);
    
    if (result) {
      onResume({
        currentStep: result.current_step,
        accountType: result.account_type,
        onboardingData: result.onboarding_data,
        sessionToken: result.session_token,
        email
      });
      onClose();
    } else {
      setError('Invalid or expired code. Please try again.');
    }
  };

  const handleForgotPassword = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter your email address first');
      return;
    }
    setResetLoading(true);
    setError('');
    try {
      const { data, error: fnError } = await supabase.functions.invoke('reset-applicant-password', {
        body: { email }
      });
      if (fnError) {
        setError('Failed to send reset email. Please try again.');
      } else {
        setResetSent(true);
        setError('');
      }
    } catch {
      setError('Failed to send reset email. Please try again.');
    }
    setResetLoading(false);
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setOtp('');
    setError('');
    setUnderReview(false);
    setResetSent(false);
    setResetLoading(false);
    setLoginMode('password');
    setOtpSent(false);
    setAccountType(null);
    setStep('account-type');
    onClose();
  };

  const handleBackToAccountType = () => {
    setStep('account-type');
    setError('');
    setUnderReview(false);
    setPassword('');
    setOtp('');
    setOtpSent(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (loginMode === 'password') {
        handlePasswordLogin();
      } else if (otpSent) {
        handleVerifyOTP();
      } else {
        handleSendOTP();
      }
    }
  };

  const switchToOTP = () => {
    setLoginMode('otp');
    setPassword('');
    setError('');
  };

  const switchToPassword = () => {
    setLoginMode('password');
    setOtp('');
    setOtpSent(false);
    setError('');
  };

  if (!isOpen) return null;

  // Step 1: Account type selection
  if (step === 'account-type') {
    return (
      <div ref={ref} className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4">
        <div className="bg-card rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in-up safe-area-inset-bottom">
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border">
            <h2 className="text-lg sm:text-xl font-semibold text-foreground">Resume Application</h2>
            <button
              onClick={handleClose}
              className="p-2.5 rounded-full hover:bg-secondary active:bg-secondary/80 transition-colors touch-manipulation"
              style={{ minWidth: '44px', minHeight: '44px' }}
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Which application would you like to resume?
            </p>
            <div className="space-y-3">
              <button
                onClick={() => handleAccountTypeSelect('individual')}
                className="w-full p-4 rounded-xl border-2 border-border bg-muted/20 hover:border-primary hover:bg-primary/5 transition-all flex items-center gap-4"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-foreground">Personal Account</p>
                  <p className="text-xs text-muted-foreground">Individual application</p>
                </div>
              </button>
              <button
                onClick={() => handleAccountTypeSelect('corporate')}
                className="w-full p-4 rounded-xl border-2 border-border bg-muted/20 hover:border-primary hover:bg-primary/5 transition-all flex items-center gap-4"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-foreground">Business Account</p>
                  <p className="text-xs text-muted-foreground">Corporate application</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Credentials entry
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-card rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in-up safe-area-inset-bottom">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackToAccountType}
              className="p-2 rounded-full hover:bg-secondary active:bg-secondary/80 transition-colors"
            >
              <ArrowRight className="w-4 h-4 text-muted-foreground rotate-180" />
            </button>
            <h2 className="text-lg sm:text-xl font-semibold text-foreground">
              Resume {accountType === 'corporate' ? 'Business' : 'Personal'} Account
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2.5 rounded-full hover:bg-secondary active:bg-secondary/80 transition-colors touch-manipulation"
            style={{ minWidth: '44px', minHeight: '44px' }}
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 pb-6 sm:pb-6">
          <div className="space-y-4">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-accent/10 flex items-center justify-center mb-3 sm:mb-4">
              {loginMode === 'password' ? (
                <Lock className="w-6 h-6 sm:w-7 sm:h-7 text-accent" />
              ) : (
                <KeyRound className="w-6 h-6 sm:w-7 sm:h-7 text-accent" />
              )}
            </div>
            <p className="text-sm sm:text-base text-muted-foreground">
              {loginMode === 'password' 
                ? 'Enter your email and password to continue where you left off.'
                : otpSent 
                  ? `We've sent a verification code to ${email}. Enter it below.`
                  : 'Enter your email to receive a verification code.'}
            </p>
            
            {/* Email Input - Always shown */}
            {(!otpSent || loginMode === 'password') && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError('');
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="you@example.com"
                    className="input-field pr-10"
                    autoFocus
                    disabled={loginMode === 'otp' && otpSent}
                  />
                  <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                </div>
              </div>
            )}

            {/* Password Input - Only for password mode */}
            {loginMode === 'password' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError('');
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter your password"
                    className="input-field pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-secondary/50 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <Eye className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={resetLoading || resetSent}
                  className="text-xs text-accent hover:underline mt-1.5 disabled:opacity-50"
                >
                  {resetLoading ? 'Sending...' : resetSent ? '✓ Reset email sent — check your inbox' : 'Forgot password?'}
                </button>
              </div>
            )}

            {/* OTP Input - Only for OTP mode after sending */}
            {loginMode === 'otp' && otpSent && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setError('');
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter 6-digit code"
                  className="input-field text-center text-2xl tracking-[0.5em] font-mono"
                  maxLength={6}
                  autoFocus
                />
                <button
                  onClick={handleSendOTP}
                  disabled={isLoading}
                  className="text-sm text-accent hover:underline mt-2"
                >
                  Resend code
                </button>
              </div>
            )}

            {underReview && (
              <div className="rounded-xl border border-warning/30 bg-warning/10 p-4 flex gap-3">
                <Clock className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-warning mb-1">Application Under Review</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Your application is currently being reviewed by our compliance team. This process may take a few hours. We'll notify you once it's complete.
                  </p>
                </div>
              </div>
            )}

            {error && !underReview && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            {/* Action Button */}
            {loginMode === 'password' ? (
              <button
                onClick={handlePasswordLogin}
                disabled={!email.includes('@') || !password || isLoading}
                className="w-full h-12 sm:h-12 rounded-full bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 active:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all touch-manipulation"
                style={{ minHeight: '48px' }}
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Login & Resume
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={otpSent ? handleVerifyOTP : handleSendOTP}
                disabled={!email.includes('@') || isLoading || (otpSent && otp.length < 6)}
                className="w-full h-12 sm:h-12 rounded-full bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 active:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all touch-manipulation"
                style={{ minHeight: '48px' }}
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : otpSent ? (
                  <>
                    Verify & Resume
                    <ArrowRight className="w-5 h-5" />
                  </>
                ) : (
                  <>
                    Send Verification Code
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            )}

            {/* Toggle between modes */}
            <div className="text-center">
              {loginMode === 'password' ? (
                <button
                  onClick={switchToOTP}
                  className="text-sm text-accent hover:underline"
                >
                  Haven't set a password? Use verification code instead
                </button>
              ) : (
                <button
                  onClick={switchToPassword}
                  className="text-sm text-accent hover:underline"
                >
                  Already have a password? Login with password
                </button>
              )}
            </div>

            <p className="text-xs text-muted-foreground text-center">
              {loginMode === 'password' 
                ? 'Use the password you created during your application.'
                : 'A verification code will be sent to your email address.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});
